import { setUrlToDataSource } from '@/tools/setUrlToDataSource';
import { logger } from '@/tools/logger';
import store from '@/store';
import { mutationTypes } from '@/types';
const imageCacheMap = new Map();

function estimateSize($_element) {
	let $element = $_element;
	while (Math.max($element.getBoundingClientRect().width, $element.getBoundingClientRect().height) === 0) {
		$element = $element.parentNode;
		// workaround if pagechange happens if component is not in the visibile dom. All nodes have no size
		// and at one point root is null. If this happens use body size as fallback
		if ($element === null) $element = document.body;
	}
	return Math.max($element.getBoundingClientRect().width, $element.getBoundingClientRect().height);
}

let packageSyncerTestResult = null;

async function preloadImage(element, immediate) {
	const url = setUrlToDataSource(element.getAttribute('imageurl'), element.getAttribute('dataSourceurl'), {
		forceRAWURL: true,
		checkForMediaFolder: true,
	});

	let size = store.state.imageSize;
	logger.log('imageloader size', size);
	if (!size) {
		if (store.state.dataSourceCFG.global.isProgressiveWebApp) {
			size = 1024;
		} else if (store.state.packageSyncer.isSyncerApp) {
			// Set image size to 1024 if packageSyncer is Active and a 2048 version has not beend downloaded previously
			// if the test was already done once use the previous result.
			if (packageSyncerTestResult === null) {
				const testUrl = url.replace(/\/[0-9]+$/, '/2048');
				const { status } = await fetch(testUrl);
				packageSyncerTestResult = status === 404 ? 1024 : 2048;
			}
			size = packageSyncerTestResult;
		} else {
			const max = estimateSize(element);
			if (max <= 512) {
				size = 512;
			} else if (max <= 1024) {
				size = 1024;
			} else if (max <= 2048) {
				size = 2048;
			} else {
				size = 1024;
			}
		}
		store.commit(mutationTypes.setImageSize, { size });
	}

	const source = url.replace(/\/[0-9]+$/, `/${size}`);
	if (!imageCacheMap.has(source) && !immediate) {
		imageCacheMap.set(source, true);
		const image = new Image();
		element.setAttribute('loading', true);
		image.setAttribute('decoding', 'image/jpeg');
		image.onload = event => {
			element.style['background-image'] = `url(${event.target.src})`;
			element.setAttribute('loaded', true);
			element.removeAttribute('loading', true);
			element.parentNode.setAttribute('loaded', true);
		};
		image.onerror = event => {
			logger.warn('error on loading image: ' + event.target.src);
			element.style['background-image'] = `url(${source})`;
			element.setAttribute('loaded', true);
			element.removeAttribute('loading', true);
			element.parentNode.setAttribute('loaded', true);
		};
		image.src = source;
	} else {
		element.style['background-image'] = `url(${source})`;
		element.setAttribute('loaded', true); // TODO
		element.parentNode.setAttribute('loaded', true);
	}
}
/**
 * init an image observer which will preload image if they are near the viewport
 * @param {*} root has to be the container which is scrolled or it has problems calucalting rootMargin
 */
export function init({ root }) {
	// eslint-disable-next-line
	const observer = new IntersectionObserver(
		(entries, observer) => {
			entries.forEach(async entry => {
				if (entry.isIntersecting) {
					const element = entry.target;
					await preloadImage(element);
					observer.unobserve(element);
				}
			});
		},
		{
			root,
			rootMargin: '100%',
			threshold: 0.0,
		}
	);
	return observer; // observer;
}
export function watch({ container, observer }) {
	const elementsWithBGs = [...container.querySelectorAll('[style]')].filter(el => el.style.backgroundImage);
	// if intersection observer is supported, wait for images to come close before loading them

	elementsWithBGs.forEach(async element => {
		if (element.getAttribute('imageURL')) return;
		element.setAttribute('dataSourceURL', store.state.dataSourceURL);
		element.setAttribute('imageURL', element.style['background-image']);
		element.style['background-image'] = null;
		// quickfix always load Header Prevent showing empty header bug
		if (
			element.parentNode &&
			element.parentNode.parentNode &&
			element.parentNode.parentNode.parentNode &&
			element.parentNode.parentNode.parentNode.nodeName === 'HEADER'
		) {
			await preloadImage(element);
		} else {
			observer.observe(element);
		}
	});
}

export function unwatch({ observer }) {
	observer.disconnect();
}
